import { sendCachedDataMessage } from "Common/MessageHandler";
import { configContext, Platform } from "ConfigContext";
import { FabricMessageTypes } from "Contracts/FabricMessageTypes";
import { CosmosDbArtifactType } from "Contracts/FabricMessagesContract";
import { updateUserContext, userContext } from "UserContext";
import { logConsoleError } from "Utils/NotificationConsoleUtils";
import * as AutoPilotUtils from "../../Utils/AutoPilotUtils";
// Fabric Native accounts are always autoscale and have a fixed throughput of 5K
export const DEFAULT_FABRIC_NATIVE_CONTAINER_THROUGHPUT = AutoPilotUtils.autoPilotThroughput5K;
const TOKEN_VALIDITY_MS = (3600 - 600) * 1000; // 1 hour minus 10 minutes to be safe
const DEBOUNCE_DELAY_MS = 1000 * 20; // 20 second
let timeoutId;
// Prevents multiple parallel requests during DEBOUNCE_DELAY_MS
let lastRequestTimestamp = undefined;
/**
 * Request fabric token:
 * - Mirrored key and AAD: Database Resource Tokens
 * - Native: AAD token
 * @returns
 */
const requestFabricToken = async () => {
    if (lastRequestTimestamp !== undefined && lastRequestTimestamp + DEBOUNCE_DELAY_MS > Date.now()) {
        return;
    }
    lastRequestTimestamp = Date.now();
    try {
        if (isFabricMirrored()) {
            await requestAndStoreDatabaseResourceTokens();
        }
        else if (isFabricNative()) {
            await requestAndStoreAccessToken();
        }
        scheduleRefreshFabricToken();
    }
    catch (error) {
        logConsoleError(error instanceof Error ? error.message : String(error));
        throw error;
    }
    finally {
        lastRequestTimestamp = undefined;
    }
};
const requestAndStoreDatabaseResourceTokens = async () => {
    var _a, _b, _c;
    if (!userContext.fabricContext || !userContext.databaseAccount) {
        // This should not happen
        logConsoleError("Fabric context or database account is missing: cannot request tokens");
        return;
    }
    const resourceTokenInfo = await sendCachedDataMessage(FabricMessageTypes.GetAllResourceTokens, [], (_a = userContext.fabricContext.artifactInfo) === null || _a === void 0 ? void 0 : _a.connectionId);
    if (!userContext.databaseAccount.properties.documentEndpoint) {
        userContext.databaseAccount.properties.documentEndpoint = resourceTokenInfo.endpoint;
    }
    if (resourceTokenInfo.credentialType === "OAuth2") {
        // Mirrored AAD
        updateUserContext({
            fabricContext: Object.assign(Object.assign({}, userContext.fabricContext), { databaseName: resourceTokenInfo.databaseId, artifactInfo: undefined, isReadOnly: (_b = resourceTokenInfo.isReadOnly) !== null && _b !== void 0 ? _b : userContext.fabricContext.isReadOnly }),
            databaseAccount: Object.assign({}, userContext.databaseAccount),
            aadToken: resourceTokenInfo.accessToken,
        });
    }
    else {
        // TODO: In Fabric contract V2, credentialType is undefined. For V3, it is "Key". Check for "Key" when V3 is supported for Fabric Mirroring Key
        // Mirrored key
        updateUserContext({
            fabricContext: Object.assign(Object.assign({}, userContext.fabricContext), { databaseName: resourceTokenInfo.databaseId, artifactInfo: Object.assign(Object.assign({}, userContext.fabricContext.artifactInfo), { resourceTokenInfo }), isReadOnly: (_c = resourceTokenInfo.isReadOnly) !== null && _c !== void 0 ? _c : userContext.fabricContext.isReadOnly }),
            databaseAccount: Object.assign({}, userContext.databaseAccount),
        });
    }
};
const requestAndStoreAccessToken = async () => {
    if (!userContext.fabricContext || !userContext.databaseAccount) {
        // This should not happen
        logConsoleError("Fabric context or database account is missing: cannot request tokens");
        return;
    }
    const accessTokenInfo = await sendCachedDataMessage(FabricMessageTypes.GetAccessToken, []);
    updateUserContext({
        aadToken: accessTokenInfo.accessToken,
    });
};
export const openRestoreContainerDialog = () => {
    if (isFabricNative()) {
        sendCachedDataMessage(FabricMessageTypes.RestoreContainer, []);
    }
};
/**
 * Check token validity and schedule a refresh if necessary
 * @param tokenTimestamp
 * @returns
 */
export const scheduleRefreshFabricToken = (refreshNow) => {
    return new Promise((resolve) => {
        if (timeoutId !== undefined) {
            clearTimeout(timeoutId);
            timeoutId = undefined;
        }
        timeoutId = setTimeout(() => {
            requestFabricToken().then(resolve);
        }, refreshNow ? 0 : TOKEN_VALIDITY_MS);
    });
};
export const checkDatabaseResourceTokensValidity = (tokenTimestamp) => {
    if (tokenTimestamp + TOKEN_VALIDITY_MS < Date.now()) {
        scheduleRefreshFabricToken(true);
    }
};
export const isFabric = () => configContext.platform === Platform.Fabric;
export const isFabricMirroredKey = () => { var _a; return isFabric() && ((_a = userContext.fabricContext) === null || _a === void 0 ? void 0 : _a.artifactType) === CosmosDbArtifactType.MIRRORED_KEY; };
export const isFabricMirroredAAD = () => { var _a; return isFabric() && ((_a = userContext.fabricContext) === null || _a === void 0 ? void 0 : _a.artifactType) === CosmosDbArtifactType.MIRRORED_AAD; };
export const isFabricMirrored = () => isFabricMirroredKey() || isFabricMirroredAAD();
export const isFabricNative = () => { var _a; return isFabric() && ((_a = userContext.fabricContext) === null || _a === void 0 ? void 0 : _a.artifactType) === CosmosDbArtifactType.NATIVE; };
export const isFabricNativeReadOnly = () => { var _a; return isFabricNative() && !!((_a = userContext.fabricContext) === null || _a === void 0 ? void 0 : _a.isReadOnly); };
